import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';

export const normalizeId = (id?: string) => id?.toLowerCase();

type OutputFeatureForApplicationMapping = {
  id?: string;
  canonicalId?: string;
  featureId?: string;
};

/** Identifiers that may appear as featureId on a table application mapping. */
export const getOutputFeatureIdsForApplicationMapping = (
  outputFeature: OutputFeatureForApplicationMapping,
): string[] => {
  const ids = [
    outputFeature.id,
    'featureId' in outputFeature ? outputFeature.featureId : undefined,
    'canonicalId' in outputFeature ? outputFeature.canonicalId : undefined,
  ].filter((id): id is string => !!id);

  return [...new Set(ids)];
};

/** Column ids mapped by any table application for the given template container. */
export const getMappedColumnIdsForTemplateContainer = (
  templateContainerId: string | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
): Set<string> => {
  const normalizedContainerId = normalizeId(templateContainerId);
  if (!normalizedContainerId || !templateColumns?.applications?.length) {
    return new Set();
  }

  const matchingApplications = templateColumns.applications.filter(
    (application) => normalizeId(application.templateContainerId) === normalizedContainerId,
  );

  const columnIds = matchingApplications.flatMap(
    (application) =>
      application.mappings?.map((mapping) => mapping.columnId).filter((columnId): columnId is string => !!columnId) ??
      [],
  );

  return new Set(columnIds);
};

/** Column id mapped to a template output feature in the first table application for the given template container. */
export const getMappedColumnIdForOutputFeatureInTemplateApplication = (
  outputFeature: OutputFeatureForApplicationMapping,
  templateContainerId: string | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
): string | undefined => {
  const normalizedContainerId = normalizeId(templateContainerId);
  if (!normalizedContainerId || !templateColumns?.applications?.length) {
    return undefined;
  }

  const application = templateColumns.applications.find(
    (app) => normalizeId(app.templateContainerId) === normalizedContainerId,
  );

  const featureIdsToMatch = new Set(
    getOutputFeatureIdsForApplicationMapping(outputFeature).map((id) => normalizeId(id)),
  );
  if (!featureIdsToMatch.size) {
    return undefined;
  }

  return application?.mappings?.find(
    (mapping) => !!mapping.featureId && featureIdsToMatch.has(normalizeId(mapping.featureId)!),
  )?.columnId;
};

export const isColumnPartOfCurrentTemplateApplication = (
  selectedColumnId: string | undefined,
  templateId: string,
  inputFeatureColumnIds: string[],
  templateColumns: TemplateTableApplicationsOutputListV1,
) => {
  const normalizedTemplateId = normalizeId(templateId);
  return (
    selectedColumnId !== undefined &&
    templateColumns?.applications?.some(
      (app) =>
        normalizeId(app.templateContainerId) === normalizedTemplateId &&
        app.mappings?.some((m) => m.columnId === selectedColumnId && !inputFeatureColumnIds.includes(selectedColumnId)),
    )
  );
};

export const isColumnInOtherTemplate = (
  selectedColumnId: string | undefined,
  templateId: string,
  inputFeatureColumnIds: string[],
  templateColumns: TemplateTableApplicationsOutputListV1,
) => {
  const normalizedTemplateId = normalizeId(templateId);
  return (
    selectedColumnId !== undefined &&
    templateColumns?.applications?.some(
      (app) =>
        normalizeId(app.templateContainerId) !== normalizedTemplateId &&
        app.mappings?.some((m) => m.columnId === selectedColumnId && !inputFeatureColumnIds.includes(selectedColumnId)),
    )
  );
};
